import {
  BaseQueryFn,
  createApi,
  fetchBaseQuery,
  retry,
} from '@reduxjs/toolkit/query/react';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { IInternalAccount } from 'src/api/internal/internalApi.types';
import { getAccountTypeKey } from 'src/utils/accountTypeUtils';
import { AccountType, EventType, TransferFlow } from 'src/utils/constants';
import { downloadBlob } from 'utils/htmlUtil';
import { API_MAX_RETRY_COUNT, createBaseUrl, setHeaders } from '..';
import { RootState } from '../../state/store';
import {
  IAtvApiConfig,
  IExternalAccount,
  IManualSalesforceRequestPayload,
  IManualTransferPayload,
  IPershingConfirmationStatus,
  ITransferConfirmationResponse,
  ITransferPayloadResponse,
  IYodleeManualTransferPayload,
  IYodleeTransferPayload,
} from './atvApi.types';

/**
 * Base query configuration for atv API calls.
 * @param args
 * @param api
 * @param extraOptions
 * @returns
 */
const atvBaseQuery: BaseQueryFn = async (args, api, extraOptions) => {
  const { appConfig } = api.getState() as RootState;
  const result = await fetchBaseQuery({
    baseUrl: createBaseUrl(appConfig, appConfig.apiBasePath),
    prepareHeaders: (headers, { getState }) => {
      return setHeaders(headers, (getState() as RootState).appConfig.apiKey);
    },
    credentials: 'include',
  })(args, api, extraOptions);
  return {
    ...result,
  };
};

const baseQueryWithRetry = retry(atvBaseQuery);

export const atvApi = createApi({
  reducerPath: 'atvApi',
  baseQuery: baseQueryWithRetry,
  tagTypes: [],
  endpoints: (builder) => ({
    getAtvToken: builder.query<IAtvApiConfig, void>({
      query: () => `atv/yodlee-token`,
      extraOptions: { maxRetries: API_MAX_RETRY_COUNT },
    }),
    getAccounts: builder.query<
      IExternalAccount[],
      { uuid: string; providerAccountID: string }
    >({
      query: ({ uuid, providerAccountID }) => `atv/accounts/${uuid}/${providerAccountID}`,
      extraOptions: { maxRetries: API_MAX_RETRY_COUNT },
    }),
    getHoldings: builder.query<
      any,
      { uuid: string; providerAccountID: string; accountID: number }
    >({
      query: ({ uuid, providerAccountID, accountID }) =>
        `atv/holdings/${uuid}/${providerAccountID}/${accountID}`,
      extraOptions: { maxRetries: API_MAX_RETRY_COUNT },
    }),
    fastLinkLog: builder.mutation<
      any,
      { message: string; eventType: EventType; id: string }
    >({
      query: (logData) => ({
        url: `fastlink/log`,
        method: 'POST',
        body: logData,
      }),
      extraOptions: { maxRetries: API_MAX_RETRY_COUNT },
    }),
    sendYodleeTransferPayload: builder.mutation<
      ITransferPayloadResponse,
      IYodleeTransferPayload
    >({
      query: (payload) => ({
        url: `atv/docusign/yodlee/signing-url`,
        method: 'POST',
        body: payload,
      }),
      extraOptions: { maxRetries: API_MAX_RETRY_COUNT },
    }),
    getTransferConfirmation: builder.query<
      ITransferConfirmationResponse,
      { confirmationNumber: string }
    >({
      query: ({ confirmationNumber }) =>
        `atv/transfer/confirmation/${confirmationNumber}`,
      extraOptions: { maxRetries: API_MAX_RETRY_COUNT },
    }),
    sendPershingConfirmationStatus: builder.mutation<
      IPershingConfirmationStatus,
      { confirmationNumber: string }
    >({
      query: ({ confirmationNumber }) => ({
        url: `atv/submit/${confirmationNumber}`,
        method: 'POST',
      }),
      extraOptions: { maxRetries: API_MAX_RETRY_COUNT },
    }),
    sendManualTransferPayload: builder.mutation<
      ITransferPayloadResponse,
      IManualTransferPayload
    >({
      query: (payload) => ({
        url: `atv/docusign/manual/signing-url`,
        method: 'POST',
        body: payload,
      }),
      extraOptions: { maxRetries: API_MAX_RETRY_COUNT },
    }),
    getTransferConfirmationDownload: builder.query<any, string>({
      query: (confirmationNumber: string) => ({
        url: `atv/transfer/confirmation/download/${confirmationNumber}`,
        headers: {
          'Content-Type': 'application/pdf',
        },
        responseHandler: async (response: any) => {
          if (!response.ok) {
            throw new Error(`Error: ${response.statusText}`);
          }
          const blob = await response.blob();
          downloadBlob(blob, 'Docusign_Form.pdf', 'application/pdf');
        },
      }),
      transformResponse: () => {
        // Return a serializable value (e.g., success flag)
        return { success: true };
      },
      extraOptions: { maxRetries: API_MAX_RETRY_COUNT },
    }),
    getEligibleManualAccounts: builder.query<
      IInternalAccount[],
      { externalAccountType: string }
    >({
      query: ({ externalAccountType }) => `atv/manual/eligible/${externalAccountType}`,
      extraOptions: { maxRetries: API_MAX_RETRY_COUNT },
    }),
    getEligibleYodleeAccounts: builder.query<
      IInternalAccount[],
      { uuid: string; providerAccountID: string; accountID: number }
    >({
      query: ({ uuid, providerAccountID, accountID }) =>
        `atv/yodlee/eligible/${uuid}/${providerAccountID}/${accountID}`,
      extraOptions: { maxRetries: API_MAX_RETRY_COUNT },
    }),
    sendYodleeManualTransferPayload: builder.mutation<
      ITransferPayloadResponse,
      IYodleeManualTransferPayload
    >({
      query: (payload) => ({
        url: `atv/docusign/yodlee-manual/signing-url`,
        method: 'POST',
        body: payload,
      }),
      extraOptions: { maxRetries: API_MAX_RETRY_COUNT },
    }),
    sendYodleeSalesforceRequestPayload: builder.mutation<
      any,
      IYodleeManualTransferPayload
    >({
      query: (payload) => ({
        url: `atv/yodlee/salesforce/event`,
        method: 'POST',
        body: payload,
      }),
      extraOptions: { maxRetries: API_MAX_RETRY_COUNT },
    }),
    sendManualSalesforceRequestPayload: builder.mutation<
      any,
      IManualSalesforceRequestPayload
    >({
      query: (payload) => ({
        url: `atv/manual/salesforce/event`,
        method: 'POST',
        body: payload,
      }),
      extraOptions: { maxRetries: API_MAX_RETRY_COUNT },
    }),
  }),
});

export const useGetAccountsBySelectedProviderQuery = () => {
  const uuid = useSelector((state: RootState) => state.yodleeConfig.uuid);
  const providerAccountID = useSelector(
    (state: RootState) => state.yodleeConfig.providerAccountID,
  );

  return atvApi.endpoints.getAccounts.useQuery({
    uuid,
    providerAccountID,
  });
};

export const useLazyGetEligibleManualAccountsProviderQuery = () => {
  const [trigger, result] = atvApi.endpoints.getEligibleManualAccounts.useLazyQuery();

  const transferFlow = useSelector((state: RootState) => state.transfer.transferFlow);
  const externalAccountTypeValue = useSelector(
    (state: RootState) => state.transfer.manualExternalAccount.accountType,
  );
  const externalAccountTypeValueIncomplete = useSelector(
    (state: RootState) => state.transfer.externalAccountIncomplete?.accountType,
  );

  const externalAccountTypeValueYodlee = useSelector(
    (state: RootState) => state.transfer.selectedExternalAccount?.selectedAccountType,
  );
  let externalAccountType: string | null | undefined;

  if (transferFlow === TransferFlow.Manual) {
    externalAccountType =
      externalAccountTypeValue !== null
        ? getAccountTypeKey(externalAccountTypeValue)
        : undefined;
  } else if (transferFlow === TransferFlow.AutomatedIncomplete) {
    externalAccountType =
      externalAccountTypeValueYodlee !== null &&
      externalAccountTypeValueYodlee !== '' &&
      externalAccountTypeValueYodlee !== AccountType[AccountType.UNDEFINED]
        ? externalAccountTypeValueYodlee
        : externalAccountTypeValueIncomplete;
  }

  const triggerQuery = useCallback(() => {
    if (externalAccountType === undefined || externalAccountType === null) {
      return { data: null, error: null, isLoading: false };
    }
    return trigger({ externalAccountType });
  }, [trigger, externalAccountType]);

  return { triggerQuery, ...result };
};

export const useLazyGetEligibleYodleeAccountsProviderQuery = () => {
  const [trigger, result] = atvApi.endpoints.getEligibleYodleeAccounts.useLazyQuery();

  const uuid = useSelector((state: RootState) => state.yodleeConfig.uuid);
  const providerAccountID = useSelector(
    (state: RootState) => state.yodleeConfig.providerAccountID,
  );
  const accountID =
    useSelector(
      (state: RootState) => state.transfer.selectedExternalAccount?.selectedId,
    ) ?? null;

  // Use useCallback with dependencies
  const triggerQuery = useCallback(() => {
    if (uuid === '' || providerAccountID === '' || accountID === null) {
      return { data: null, error: null, isLoading: false };
    }
    return trigger({ uuid, providerAccountID, accountID });
  }, [trigger, uuid, providerAccountID, accountID]);

  return { triggerQuery, ...result };
};

export const useGetHoldingsBySelectedAccountQuery = () => {
  const uuid = useSelector((state: RootState) => state.yodleeConfig.uuid);
  const providerAccountID = useSelector(
    (state: RootState) => state.yodleeConfig.providerAccountID,
  );
  const accountID =
    useSelector(
      (state: RootState) => state.transfer.selectedExternalAccount?.selectedId,
    ) ?? null;

  if (uuid === '' || providerAccountID === '' || accountID === null) {
    return { data: null, error: 'One or more required values are null' };
  }

  return atvApi.endpoints.getHoldings.useQuery({
    uuid,
    providerAccountID,
    accountID,
  });
};

export const useGetTransferConfirmationQuery = (confirmationNumber: string | null) => {
  return confirmationNumber
    ? atvApi.endpoints.getTransferConfirmation.useQuery({
        confirmationNumber,
      })
    : { data: null, isLoading: false, error: null }; // Return a default object when confirmationNumber is null
};

export const useSendPershingConfirmationStatus = () => {
  return atvApi.endpoints.sendPershingConfirmationStatus.useMutation();
};

export const useFastLinkLog = () => {
  const [fastLinkLog] = atvApi.endpoints.fastLinkLog.useMutation();
  return { fastLinkLog };
};

export const useSendYodleeSalesforceTransfer = () => {
  const [sendYodleeSalesforceRequestPayload] =
    atvApi.endpoints.sendYodleeSalesforceRequestPayload.useMutation();
  return { sendYodleeSalesforceRequestPayload };
};

export const useSendManualSalesforceTransfer = () => {
  const [sendManualSalesforceRequestPayload] =
    atvApi.endpoints.sendManualSalesforceRequestPayload.useMutation();
  return { sendManualSalesforceRequestPayload };
};

export const {
  useGetAtvTokenQuery,
  useGetAccountsQuery,
  useGetHoldingsQuery,
  useFastLinkLogMutation,
  useLazyGetAccountsQuery,
  useLazyGetHoldingsQuery,
  useLazyGetTransferConfirmationDownloadQuery,
  useSendYodleeTransferPayloadMutation,
  useSendManualTransferPayloadMutation,
  useSendYodleeManualTransferPayloadMutation,
  useLazyGetEligibleManualAccountsQuery,
  useLazyGetEligibleYodleeAccountsQuery,
  useSendManualSalesforceRequestPayloadMutation,
  useSendYodleeSalesforceRequestPayloadMutation,
  useLazyGetTransferConfirmationQuery,
} = atvApi;
